import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api, CARE_TYPES, FACILITY_TYPES, SPECIALTIES, CARE_NEEDS, FINANCIAL, AVAILABILITY, US_STATES, stateSlug, careTypeLabel, facilityTypeLabel, specialtyLabel, fileUrl } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { MapPin, Search as SearchIcon, Phone, Mail, Clock, DollarSign, CalendarCheck } from "lucide-react";

const PLACEHOLDER_IMG =
  "https://images.unsplash.com/photo-1758691031197-3ce709db9640?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODh8MHwxfHNlYXJjaHwxfHxjb3p5JTIwbGl2aW5nJTIwcm9vbSUyMHNlbmlvciUyMGNvbW11bml0eXxlbnwwfHx8fDE3ODAxNDU3NzB8MA&ixlib=rb-4.1.0&q=85&w=800";

export default function Search() {
  const [params, setParams] = useSearchParams();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [location, setLocation] = useState(params.get("city") || params.get("zip") || "");
  const [careType, setCareType] = useState(params.get("care_type") || "");
  const [facilityType, setFacilityType] = useState(params.get("facility_type") || "");
  const [specialty, setSpecialty] = useState(params.get("specialty") || "");
  const [stateFilter, setStateFilter] = useState(params.get("state") || "");
  const [careNeed, setCareNeed] = useState(params.get("care_need") || "");
  const [financialOpt, setFinancialOpt] = useState(params.get("financial_opt") || "");
  const [available, setAvailable] = useState(params.get("available") || "");

  const load = async () => {
    setLoading(true);
    try {
      const query = {};
      if (params.get("city")) query.city = params.get("city");
      if (params.get("zip")) query.zip = params.get("zip");
      if (params.get("state")) query.state = params.get("state");
      if (params.get("care_type")) query.care_type = params.get("care_type");
      if (params.get("facility_type")) query.facility_type = params.get("facility_type");
      if (params.get("specialty")) query.specialty = params.get("specialty");
      if (params.get("care_need")) query.care_need = params.get("care_need");
      if (params.get("financial_opt")) query.financial_opt = params.get("financial_opt");
      if (params.get("available")) query.available = params.get("available");
      if (params.get("q")) query.q = params.get("q");
      const { data } = await api.get("/facilities", { params: query });
      setItems(data.items || []);
    } catch (e) {
      setItems([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    document.title = "Find Adult Day Care & Memory Care — Providence Partners";
    let meta = document.querySelector('meta[name="description"]');
    if (meta) {
      meta.setAttribute(
        "content",
        "Search vetted adult day care, memory care, respite, and autism/IDD homes for families seeking care. Filter by state, city, or ZIP nationwide.",
      );
    }
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params.toString()]);

  const onSearch = (e) => {
    e.preventDefault();
    const next = new URLSearchParams();
    if (location.trim()) {
      if (/^\d{5}$/.test(location.trim())) next.set("zip", location.trim());
      else next.set("city", location.trim());
    }
    if (careType) next.set("care_type", careType);
    if (facilityType) next.set("facility_type", facilityType);
    if (specialty) next.set("specialty", specialty);
    if (stateFilter) next.set("state", stateFilter);
    if (careNeed) next.set("care_need", careNeed);
    if (financialOpt) next.set("financial_opt", financialOpt);
    if (available) next.set("available", available);
    setParams(next);
  };

  return (
    <div data-testid="search-page" className="mx-auto max-w-7xl px-6 py-10 lg:px-8">
      <div className="max-w-2xl">
        <p className="text-sm uppercase tracking-[0.18em] text-primary">Find a center</p>
        <h1 className="mt-2 font-serif text-4xl text-forest sm:text-5xl">
          Adult Day Care & Respite Homes Near You
        </h1>
        <p className="mt-3 text-muted-foreground">
          Search verified member homes by city, ZIP, or state. Filter by drop-in, weekly respite, or trial
          visit availability.
        </p>
        <Link to="/states" data-testid="search-browse-states-link" className="mt-3 inline-flex items-center gap-1 text-sm font-medium text-primary hover:underline">
          <MapPin className="h-3.5 w-3.5" /> Browse by state →
        </Link>
      </div>

      <form
        data-testid="search-filter-form"
        onSubmit={onSearch}
        className="mt-8 grid gap-3 rounded-2xl border border-border/60 bg-card p-4 shadow-sm md:grid-cols-12"
      >
        <div className="md:col-span-3 flex items-center gap-2 rounded-xl border border-border bg-background px-3">
          <MapPin className="h-4 w-4 text-primary" />
          <Input
            data-testid="search-location-input"
            placeholder="City or ZIP"
            value={location}
            onChange={(e) => setLocation(e.target.value)}
            className="h-11 border-0 bg-transparent shadow-none focus-visible:ring-0"
          />
        </div>
        <select
          data-testid="search-state-select"
          value={stateFilter}
          onChange={(e) => setStateFilter(e.target.value)}
          className="md:col-span-2 h-11 rounded-xl border border-border bg-background px-3 text-sm"
        >
          <option value="">Any state</option>
          {US_STATES.map((s) => (
            <option key={s.code} value={s.code}>{s.name}</option>
          ))}
        </select>
        <select
          data-testid="search-care-type-select"
          value={careType}
          onChange={(e) => setCareType(e.target.value)}
          className="md:col-span-2 h-11 rounded-xl border border-border bg-background px-3 text-sm"
        >
          <option value="">Any care type</option>
          {CARE_TYPES.map((c) => (
            <option key={c.value} value={c.value}>{c.label}</option>
          ))}
        </select>
        <select
          data-testid="search-specialty-select"
          value={specialty}
          onChange={(e) => setSpecialty(e.target.value)}
          className="md:col-span-2 h-11 rounded-xl border border-border bg-background px-3 text-sm"
        >
          <option value="">Any specialty</option>
          {SPECIALTIES.map((c) => (
            <option key={c.value} value={c.value}>{c.label}</option>
          ))}
        </select>
        <select
          data-testid="search-facility-type-select"
          value={facilityType}
          onChange={(e) => setFacilityType(e.target.value)}
          className="md:col-span-1 h-11 rounded-xl border border-border bg-background px-3 text-sm"
        >
          <option value="">Any type</option>
          {FACILITY_TYPES.map((c) => (
            <option key={c.value} value={c.value}>{c.label}</option>
          ))}
        </select>
        <Button
          type="submit"
          data-testid="search-submit-btn"
          className="md:col-span-2 h-11 rounded-xl bg-primary text-primary-foreground"
        >
          <SearchIcon className="mr-2 h-4 w-4" /> Search
        </Button>
      </form>

      <div className="mt-8 flex items-center justify-between">
        <p className="text-sm text-muted-foreground" data-testid="search-result-count">
          {loading ? "Searching…" : `${items.length} member home${items.length === 1 ? "" : "s"} found`}
        </p>
      </div>

      <div className="mt-4 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
        {items.map((f) => {
          const cover = f.photos && f.photos.length > 0 ? fileUrl(f.photos[0].path) : PLACEHOLDER_IMG;
          return (
            <Card
              key={f.id}
              data-testid={`facility-card-${f.id}`}
              className="overflow-hidden rounded-2xl border border-border/60 bg-card shadow-sm transition-transform hover:-translate-y-1"
            >
              <div className="relative aspect-[5/3] w-full overflow-hidden bg-muted">
                <img src={cover} alt={f.name} className="h-full w-full object-cover" />
                <Badge className="absolute left-3 top-3 rounded-full bg-card/95 text-xs text-foreground shadow">
                  {facilityTypeLabel(f.facility_type)}
                </Badge>
                {(f.specialties || []).includes("memory_care") && (
                  <Badge className="absolute right-3 top-3 rounded-full bg-primary text-xs text-primary-foreground shadow" data-testid={`memory-care-badge-${f.id}`}>
                    Memory Care
                  </Badge>
                )}
              </div>
              <div className="p-5">
                <h3 className="font-serif text-2xl text-forest leading-tight">{f.name}</h3>
                <p className="mt-1 flex items-center gap-1.5 text-sm text-muted-foreground">
                  <MapPin className="h-3.5 w-3.5" />
                  {f.city}, {f.state} {f.zip}
                </p>
                {(f.specialties || []).length > 0 && (
                  <div className="mt-3 flex flex-wrap gap-1.5">
                    {(f.specialties || []).map((s) => (
                      <span key={s} className="pill border border-emerald/30 bg-emerald/10 text-emerald">
                        {specialtyLabel(s)}
                      </span>
                    ))}
                  </div>
                )}
                <div className="mt-3 flex flex-wrap gap-1.5">
                  {(f.care_types || []).map((c) => (
                    <span key={c} className="pill bg-secondary text-secondary-foreground">
                      {careTypeLabel(c)}
                    </span>
                  ))}
                </div>
                <p className="mt-4 line-clamp-2 text-sm text-muted-foreground">{f.description}</p>
                <div className="mt-4 flex items-center justify-between text-xs text-muted-foreground">
                  <span className="inline-flex items-center gap-1"><Clock className="h-3 w-3" /> {f.hours || "Hours vary"}</span>
                  <span className="inline-flex items-center gap-1"><DollarSign className="h-3 w-3" /> {f.price_range || "Contact"}</span>
                </div>
                <Button
                  asChild
                  className="mt-5 w-full rounded-full bg-primary text-primary-foreground"
                  data-testid={`facility-view-btn-${f.id}`}
                >
                  <Link to={`/facility/${f.id}`}>View & inquire</Link>
                </Button>
              </div>
            </Card>
          );
        })}
        {!loading && items.length === 0 && (
          <Card className="col-span-full rounded-2xl border border-gold/40 bg-gold/5 p-12 text-center" data-testid="search-empty-state">
            <Phone className="mx-auto h-10 w-10 text-gold" strokeWidth={1.5} />
            <p className="mt-4 font-serif text-3xl text-forest">No homes in your area yet — but we can still help.</p>
            <p className="mx-auto mt-3 max-w-xl text-muted-foreground">
              Our member network is still growing nationwide. Call a Providence Partners
              advisor directly and we'll personally help you find adult day care, drop-in, respite,
              memory care, autism/IDD support, or daily hospice respite for your loved one today.
            </p>
            <Button asChild className="mt-6 rounded-full bg-primary px-7 py-6 text-base text-primary-foreground" data-testid="search-empty-call-btn">
              <a href="tel:+15863068449"><Phone className="mr-2 h-4 w-4" /> Call (586) 306-8449</a>
            </Button>
            <p className="mt-2 text-xs italic text-muted-foreground">Always available — 24/7</p>
            <p className="mt-3 text-xs text-muted-foreground">Or <Link to="/placement-help" className="text-primary hover:underline">request placement help online</Link>.</p>
          </Card>
        )}
      </div>
    </div>
  );
}
